import {AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, ViewEncapsulation} from '@angular/core';
import {UIDarstellungsart} from '../../../models/uiDarstellungsart';
import {flatMap, UIArtefakt, UIArtefaktGruppe, UiInputControl, UIMenu, UIMenuSeite, uniqueKey} from '../../../models/uiArtefakt';
import {Observable, Subscription} from 'rxjs';
import {filter, map, withLatestFrom} from 'rxjs/operators';
import {EventService} from '../../../services/event.service';
import {MenuGruppe, MenuSeite} from '../../../models/menuState';
import {ShellConfigService} from '../../../services/shell-config.service';
import {UILayout} from '../../../models/uiLayout';
import {MatTabChangeEvent} from '@angular/material/tabs';
import {UIAusrichtung} from '../../../models/uiAusrichtung';
import {TrackingService} from '../../../services/tracking.service';
import {TrackingAktion, TrackingCategory, TrackingName} from '../../../models/enumTracking';

@Component({
    selector: 'lv-tab-view',
    templateUrl: './tab-view.component.html',
    styleUrls: ['./tab-view.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class TabViewComponent implements OnInit, OnDestroy, AfterViewInit  {
  @Input()
  public uiArtefakt: UIArtefaktGruppe;
  @Input()
  public menu: UIMenu;
  @Input()
  public uiLayout: UILayout;

  public menuIndex$: Observable<number>;
  public uiDarstellungsart = UIDarstellungsart;
  public uiLayoutEnum = UILayout;
  public showMenu: boolean;
  public showWorkflowButtons: boolean;
  public uiAusrichtung: UIAusrichtung;
  public uiControlsRequestPending: boolean;
  public clickedButton: string;
  public hasPflichtfelder: Map<string, boolean> = new Map<string, boolean>();

  private _uiControlsRequestPendingSubscription$ = Subscription.EMPTY;
  private _subscription: Subscription = Subscription.EMPTY;


  public constructor(private _eventService: EventService,
                     private _shellConfigService: ShellConfigService,
                     private _renderer: Renderer2,
                     private _element: ElementRef,
                     private _tracker: TrackingService) {
  }

  ngAfterViewInit(): void {
  }

  ngOnInit(): void {
    this.showMenu = this._shellConfigService.shellParameter.showMenu;
    this.showWorkflowButtons = this._shellConfigService.shellParameter.showWorkflowButtons;
    this.uiAusrichtung = this._shellConfigService.getParameter().uiAusrichtung;
    this._subscription = this._eventService.menuItemSelected$.subscribe(_ => window.scroll(0, 0));
    this.menuIndex$ = this._eventService.menuItemSelected$.pipe(
        withLatestFrom(this._eventService.menu$),
        filter(([menuSeiteSelected, _]) => !!menuSeiteSelected),
        map(([menuSeiteSelected, menu]) => {
          this.clickedButton = void 0;
          if (this.uiLayout === UILayout.NAVIGATION_MENU) {
            const alleSeiten: UIMenuSeite[] = [];
            menu.UIMenuGruppen.forEach(menugruppe => alleSeiten.push(...menugruppe.UIMenuSeiten));
            return alleSeiten.findIndex(seite => menuSeiteSelected === seite.Identifikator);
          } else if (this.uiLayout === UILayout.TAB_MENU_NACH_GRUPPEN) {
            return menu.UIMenuGruppen.findIndex(gruppe => gruppe.Identifikator === menuSeiteSelected);
          }
          return 0;
        })
    );
    this._uiControlsRequestPendingSubscription$ = this._eventService.uiControlsRequestPending$
    .subscribe(requestPending => this.uiControlsRequestPending = requestPending);

    this._hasPflichtfelder();
  }

  public onSelectedFocusChange(matTabChangeEvent: MatTabChangeEvent) {
    this._eventService.menuItemRequested$.next(this.menu.UIMenuGruppen[matTabChangeEvent.index]?.Identifikator);
  }

  public onZurueckClicked(menuTitle: string) {
    this._tracker.trackEvent(TrackingCategory.NAVIGATION, TrackingAktion.T_BUTTON, TrackingName.ZURUECK + '_' +  menuTitle);
    this.clickedButton = 'zurueck';
    this._eventService.zurueckButtonClicked$.next();
  }

  public onWeiterClicked(menuTitle: string) {
    this._tracker.trackEvent(TrackingCategory.NAVIGATION, TrackingAktion.T_BUTTON, TrackingName.WEITER + '_' + menuTitle);
    this.clickedButton = 'weiter';
    this._eventService.weiterButtonClicked$.next();
  }

  public trackBySeite(item: MenuSeite) {
    return item.Identifikator;
  }

  public trackByGruppe(item: MenuGruppe) {
    return item.Identifikator;
  }

  public trackByFn(index: number, item: UIArtefakt) {
    return uniqueKey(item);
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
    if (!!this._uiControlsRequestPendingSubscription$) {
      this._uiControlsRequestPendingSubscription$.unsubscribe();
    }
  }

  private _hasPflichtfelder() {
    if (!!this.menu.UIMenuGruppen) {
      this.menu.UIMenuGruppen.forEach(gruppe => {
        this.hasPflichtfelder.set(gruppe.Identifikator, false);
        gruppe.UIMenuSeiten.forEach(seite => {
          const pflichtFelder = flatMap(seite.UIArtefakt).map(artefakt => artefakt as UiInputControl)
          .filter(artefakt => !!artefakt.PflichtfeldJN && artefakt.PflichtfeldJN).length;
          if (pflichtFelder > 0) {
            this.hasPflichtfelder.set(seite.Identifikator, true);
            this.hasPflichtfelder.set(gruppe.Identifikator, true);
          } else {
            this.hasPflichtfelder.set(seite.Identifikator, false);
          }
        });
      });
    }
  }
}
